import { Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useOutlookCalendars } from '../../hooks/useWidgetsData';
import { startMicrosoft, useSession } from '../../store/session';
import { newId, useSettings, type CalendarSource, type FeedSource } from '../../store/settings';
import { buttonClass, Choice, ghostButtonClass, inputClass, Section, Toggle, WidgetPage } from './controls';

const CAL_COLORS = ['#38bdf8', '#f472b6', '#a3e635', '#fbbf24', '#c084fc', '#f87171'];

function validFeedUrl(raw: string): string | null {
  try {
    const u = new URL(raw.trim().replace(/^webcals?:\/\//i, 'https://'));
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
  } catch {
    return null;
  }
}

function SourceForm({ placeholder, onAdd }: { placeholder: string; onAdd: (name: string, url: string) => void }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const valid = validFeedUrl(url);
    if (!valid) {
      setError('Enter a valid web address.');
      return;
    }
    onAdd(name.trim() || new URL(valid).hostname, valid);
    setName('');
    setUrl('');
    setError(null);
  };
  return (
    <form onSubmit={submit} className="space-y-2">
      <input className={inputClass} placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" />
      <div className="flex gap-2">
        <input className={inputClass} placeholder={placeholder} value={url} onChange={(e) => setUrl(e.target.value)} aria-label="Address" />
        <button className={buttonClass} type="submit">
          Add
        </button>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
    </form>
  );
}

function SourceRow<T extends FeedSource>({
  source,
  onChange,
  onRemove,
  swatch,
}: {
  source: T;
  onChange: (s: T) => void;
  onRemove: () => void;
  swatch?: string;
}) {
  return (
    <li className="flex items-center gap-3 rounded-lg border border-[var(--border)] px-3 py-2">
      {swatch && <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: swatch }} />}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{source.name}</div>
        <div className="muted truncate text-xs">{source.url.replace(/\?.*$/, '?…')}</div>
      </div>
      <button
        role="switch"
        aria-checked={source.enabled}
        aria-label={`Show ${source.name}`}
        onClick={() => onChange({ ...source, enabled: !source.enabled })}
        className={`h-5 w-9 shrink-0 rounded-full transition ${source.enabled ? 'bg-[var(--accent)]' : 'bg-[var(--border)]'}`}
      >
        <span className={`block h-4 w-4 rounded-full bg-white transition ${source.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
      </button>
      <button onClick={onRemove} aria-label={`Remove ${source.name}`} className="opacity-60 hover:opacity-100">
        <Trash2 className="h-4 w-4" aria-hidden />
      </button>
    </li>
  );
}

/** Every calendar on the connected Microsoft account, each switchable. */
function OutlookCalendarList() {
  const hidden = useSettings((s) => s.calendar.outlookHidden);
  const update = useSettings((s) => s.update);
  const { data, error, loading } = useOutlookCalendars();
  if (!data) return <p className="muted pl-1 text-xs">{loading ? 'Loading your calendars…' : (error ?? '')}</p>;
  const toggle = (id: string) =>
    update('calendar', { outlookHidden: hidden.includes(id) ? hidden.filter((h) => h !== id) : [...hidden, id] });
  return (
    <ul className="space-y-1.5 pl-1">
      {data.map((c) => (
        <li key={c.id}>
          <label className="flex cursor-pointer items-center gap-3 text-sm">
            <input type="checkbox" checked={!hidden.includes(c.id)} onChange={() => toggle(c.id)} className="accent-[var(--accent)]" />
            <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: c.color }} aria-hidden />
            <span className="min-w-0 flex-1 truncate">
              {c.name}
              {c.owner && !c.isDefault && <span className="muted text-xs"> · {c.owner}</span>}
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
}

export function CalendarTab() {
  const calendar = useSettings((s) => s.calendar);
  const update = useSettings((s) => s.update);
  const { config, user, microsoft } = useSession();
  const setSources = (sources: CalendarSource[]) => update('calendar', { sources });

  return (
    <WidgetPage label="Show calendar" enabled={calendar.enabled} onChange={(enabled) => update('calendar', { enabled })}>
      <Section title="Outlook & view">
        {config?.features.microsoft &&
          (microsoft?.connected ? (
            <>
              <Toggle label="Outlook calendars" checked={calendar.outlook} onChange={(outlook) => update('calendar', { outlook })} />
              {calendar.outlook && <OutlookCalendarList />}
            </>
          ) : (
            <div className="flex items-center justify-between gap-4 text-sm">
              <span>Outlook calendar</span>
              {user ? (
                <button className="text-[var(--accent)] hover:underline" onClick={() => startMicrosoft('link')}>
                  Connect Microsoft
                </button>
              ) : (
                <span className="muted text-xs">Sign in to connect</span>
              )}
            </div>
          ))}
        <Choice
          options={[
            { id: 'list', name: 'List', description: 'Up next' },
            { id: 'day', name: 'Day', description: 'Today by hour' },
            { id: 'week', name: 'Week', description: '7 days' },
            { id: 'month', name: 'Month', description: 'Whole month' },
          ]}
          value={calendar.view}
          onChange={(view) => update('calendar', { view })}
          columns={4}
        />
        {(calendar.view === 'week' || calendar.view === 'month') && (
          <Toggle
            label="Weeks start on Monday"
            checked={calendar.weekStartsOn === 1}
            onChange={(monday) => update('calendar', { weekStartsOn: monday ? 1 : 0 })}
          />
        )}
        {calendar.view === 'list' && (
        <label className="block text-sm">
          <div className="mb-1 flex justify-between">
            <span>Days ahead</span>
            <span className="muted">{calendar.days}</span>
          </div>
          <input
            type="range"
            min={1}
            max={30}
            value={calendar.days}
            onChange={(e) => update('calendar', { days: Number(e.target.value) })}
            className="w-full accent-[var(--accent)]"
          />
        </label>
        )}
        <p className="muted text-xs">Week and Month views look best in the Bento or a portrait layout, where the calendar is wider.</p>
      </Section>
      <Section title="Style">
        <Choice
          options={[
            { id: '0.85', name: 'S' },
            { id: '1', name: 'M' },
            { id: '1.2', name: 'L' },
            { id: '1.4', name: 'XL' },
          ]}
          value={String(calendar.textSize)}
          onChange={(v) => update('calendar', { textSize: Number(v) })}
          columns={4}
        />
        <Choice
          options={[
            { id: 'dot', name: 'Dot', description: 'A coloured dot' },
            { id: 'bar', name: 'Bar', description: 'A coloured edge' },
            { id: 'block', name: 'Block', description: 'Tinted in its colour' },
          ]}
          value={calendar.eventStyle}
          onChange={(eventStyle) => update('calendar', { eventStyle })}
          columns={3}
        />
        <Toggle label="Title (e.g. “Up next”)" checked={calendar.showHeader} onChange={(showHeader) => update('calendar', { showHeader })} />
        <Toggle label="Event locations" checked={calendar.showLocation} onChange={(showLocation) => update('calendar', { showLocation })} />
        <Toggle label="End times" checked={calendar.showEndTime} onChange={(showEndTime) => update('calendar', { showEndTime })} />
        <div className="flex items-center gap-3 text-sm">
          <input
            type="color"
            value={calendar.todayColor || '#38bdf8'}
            onChange={(e) => update('calendar', { todayColor: e.target.value })}
            className="h-10 w-14 shrink-0 cursor-pointer rounded border border-[var(--border)] bg-transparent"
            aria-label="Today colour"
          />
          <span className="flex-1">Colour for today</span>
          {calendar.todayColor && (
            <button type="button" className={ghostButtonClass} onClick={() => update('calendar', { todayColor: '' })}>
              Use theme accent
            </button>
          )}
        </div>
      </Section>
      <Section
        title="Calendars"
        description="Paste a private iCal (.ics / webcal://) address. iCloud: Calendar → share → Public Calendar. Google: Settings → calendar → Secret address in iCal format. Outlook: Settings → Shared calendars → Publish. Signing in with Microsoft or Google will connect calendars directly once available."
      >
        <ul className="space-y-2">
          {calendar.sources.map((s) => (
            <SourceRow
              key={s.id}
              source={s}
              swatch={s.color}
              onChange={(n) => setSources(calendar.sources.map((x) => (x.id === s.id ? n : x)))}
              onRemove={() => setSources(calendar.sources.filter((x) => x.id !== s.id))}
            />
          ))}
        </ul>
        <SourceForm
          placeholder="webcal://… or https://….ics"
          onAdd={(name, url) =>
            setSources([
              ...calendar.sources,
              { id: newId(), name, url, enabled: true, color: CAL_COLORS[calendar.sources.length % CAL_COLORS.length]! },
            ])
          }
        />
        <p className="muted text-xs">Calendar addresses are private. They are fetched through the KCL Canvas server and never shown on screen.</p>
      </Section>
    </WidgetPage>
  );
}

export function NewsTab() {
  const news = useSettings((s) => s.news);
  const update = useSettings((s) => s.update);
  const setSources = (sources: FeedSource[]) => update('news', { sources });

  return (
    <WidgetPage
      label="Show news"
      description="Tap any headline or story to open it with a link and a QR code for your phone."
      enabled={news.enabled}
      onChange={(enabled) => update('news', { enabled })}
    >
      <Section title="Display">
        <Choice
          options={[
            { id: 'ticker', name: 'Ticker', description: 'Scrolling headlines' },
            { id: 'card', name: 'Story card', description: 'Rotating picture + summary' },
            { id: 'both', name: 'Both' },
          ]}
          value={news.display}
          onChange={(display) => update('news', { display })}
          columns={3}
        />
        {news.display !== 'ticker' && (
          <label className="block text-sm">
            <div className="mb-1 flex justify-between">
              <span>Next story every</span>
              <span className="muted">{news.cardIntervalSec} s</span>
            </div>
            <input
              type="range"
              min={8}
              max={120}
              step={2}
              value={news.cardIntervalSec}
              onChange={(e) => update('news', { cardIntervalSec: Number(e.target.value) })}
              className="w-full accent-[var(--accent)]"
            />
          </label>
        )}
        <label className="block text-sm">
          <div className="mb-1 flex justify-between">
            <span>Ticker speed</span>
            <span className="muted">{news.speed < 45 ? 'Slow' : news.speed < 90 ? 'Medium' : 'Fast'}</span>
          </div>
          <input
            type="range"
            min={20}
            max={150}
            value={news.speed}
            onChange={(e) => update('news', { speed: Number(e.target.value) })}
            className="w-full accent-[var(--accent)]"
          />
        </label>
      </Section>
      <Section title="Feeds" description="Any RSS or Atom feed address.">
        <ul className="space-y-2">
          {news.sources.map((s) => (
            <SourceRow
              key={s.id}
              source={s}
              onChange={(n) => setSources(news.sources.map((x) => (x.id === s.id ? n : x)))}
              onRemove={() => setSources(news.sources.filter((x) => x.id !== s.id))}
            />
          ))}
        </ul>
        <SourceForm
          placeholder="https://…/rss.xml"
          onAdd={(name, url) => setSources([...news.sources, { id: newId(), name, url, enabled: true }])}
        />
      </Section>
    </WidgetPage>
  );
}
